/**
 * Starea goala spune ce zi e goala si ce se poate face — nu „No data".
 * Daca golul vine din filtre, prima actiune e mereu stergerea lor, cu numarul scris: o grila
 * filtrata care arata zero e cel mai frecvent „bug" raportat intr-un instrument de trezorerie.
 */

import type { ReactNode } from 'react';
import { formatDayLabel } from '@evomag/core/format';

export interface ActiuneGol {
  eticheta: string;
  onClick: () => void;
  /** Actiunea principala primeste contur mai puternic. */
  principala?: boolean;
}

export interface PropsEmptyState {
  titlu: string;
  descriere?: ReactNode;
  actiuni?: readonly ActiuneGol[];
  /** Numarul de filtre active; daca > 0 apare „Sterge filtrele (N)". */
  filtreActive?: number;
  onStergeFiltre?: () => void;
  className?: string;
}

export function EmptyState({
  titlu,
  descriere,
  actiuni = [],
  filtreActive = 0,
  onStergeFiltre,
  className,
}: PropsEmptyState) {
  return (
    <div
      className={`flex flex-col items-center gap-3 px-4 py-10 text-center ${className ?? ''}`}
      role="status"
    >
      <p className="text-16 font-medium text-ink-1">{titlu}</p>
      {descriere ? <p className="max-w-prose text-13 text-ink-2">{descriere}</p> : null}

      <div className="flex flex-wrap items-center justify-center gap-2">
        {filtreActive > 0 && onStergeFiltre ? (
          <button type="button" className="hit-24 chip font-medium text-ink-1" onClick={onStergeFiltre}>
            Șterge filtrele ({filtreActive})
          </button>
        ) : null}

        {actiuni.map((a) => (
          <button
            key={a.eticheta}
            type="button"
            className={`hit-24 chip ${a.principala ? 'font-medium text-ink-1' : ''}`}
            onClick={a.onClick}
          >
            {a.eticheta}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Varianta pentru ecranul zilei, cu exact copia din plan:
 * „Nicio plata planificata pentru Vineri, 14.08.2026."
 */
export function GolPlanZi({
  dataIso,
  filtreActive = 0,
  onStergeFiltre,
  onAdauga,
  onCopiazaZiPrecedenta,
  onVeziMatrice,
}: {
  dataIso: string;
  filtreActive?: number;
  onStergeFiltre?: () => void;
  onAdauga: () => void;
  onCopiazaZiPrecedenta: () => void;
  onVeziMatrice: () => void;
}) {
  const etichetaZi = formatDayLabel(dataIso).replace(' ', ', ');
  return (
    <EmptyState
      titlu={`Nicio plată planificată pentru ${etichetaZi}.`}
      descriere="Ziua există, dar nu are nicio intrare. Nu este același lucru cu „planificat zero”."
      {...(onStergeFiltre ? { onStergeFiltre } : {})}
      filtreActive={filtreActive}
      actiuni={[
        { eticheta: 'Adaugă o plată', onClick: onAdauga, principala: true },
        { eticheta: 'Copiază planul din ziua lucrătoare precedentă', onClick: onCopiazaZiPrecedenta },
        { eticheta: 'Vezi matricea furnizor × zi', onClick: onVeziMatrice },
      ]}
    />
  );
}
